import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ThreeViewer } from './ThreeViewer';
import { ThietKe3DData } from '../types';
import * as api from '../api';
import Spinner from '@/components/common/Spinner';
import { 
  ChevronLeftIcon, 
  HelpCircleIcon, 
  InfoIcon, 
  LayersIcon, 
  EyeIcon, 
  PlayCircleIcon,
  CheckIcon,
  MaximizeIcon,
  MinimizeIcon
} from '@/components/common/Icons';

export const ThietKe3DPlayer: React.FC = () => {
  const { slugWithId } = useParams<{ slugWithId: string }>();
  const navigate = useNavigate();
  const [appData, setAppData] = useState<ThietKe3DData | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [activeEntityId, setActiveEntityId] = useState<string | null>(null);
  const [exploded, setExploded] = useState(false);
  const [sectionView, setSectionView] = useState(false);
  const [showInfo, setShowInfo] = useState(false);
  const [activeQuizId, setActiveQuizId] = useState<string | null>(null);
  const [quizStatus, setQuizStatus] = useState<'pending' | 'correct' | 'incorrect'>('pending');
  const [isFullscreen, setIsFullscreen] = useState(false);

  useEffect(() => {
    if (!slugWithId) return;
    
    // Extract ID from slug-i{ID}.html
    const match = slugWithId.match(/i(\d+)(\.html)?$/);
    const id = match ? match[1] : slugWithId;

    api.getApp(id)
      .then(app => {
        setAppData(app.data);
      })
      .catch(err => {
        console.error('Lỗi khi tải ứng dụng 3D:', err);
      })
      .finally(() => setIsLoading(false));
  }, [slugWithId]);

  const toggleFullscreen = () => {
    if (!document.fullscreenElement) {
      document.documentElement.requestFullscreen();
      setIsFullscreen(true);
    } else {
      if (document.exitFullscreen) {
        document.exitFullscreen();
        setIsFullscreen(false);
      }
    }
  };

  const handleEntityClick = (entityId: string) => {
    setActiveEntityId(entityId);
    if (activeQuizId && appData) {
      const quiz = appData.quizzes.find(q => q.id === activeQuizId);
      if (quiz && quiz.targetEntityId === entityId) {
        setQuizStatus('correct');
      } else {
        setQuizStatus('incorrect');
      }
    }
  };

  if (isLoading) {
    return (
      <div className="fixed inset-0 bg-slate-900 flex flex-col items-center justify-center text-white gap-4 z-50">
        <Spinner className="w-12 h-12 text-indigo-500" />
        <p className="text-lg font-black tracking-widest uppercase animate-pulse">Đang khởi tạo không gian 3D...</p>
      </div>
    );
  }

  if (!appData) {
    return (
      <div className="fixed inset-0 bg-slate-900 flex flex-col items-center justify-center text-white gap-6 z-50 p-8 text-center">
        <div className="w-20 h-20 bg-rose-500/20 text-rose-500 rounded-full flex items-center justify-center text-4xl">⚠️</div>
        <h1 className="text-2xl font-black">Không tìm thấy ứng dụng 3D</h1>
        <button onClick={() => navigate(-1)} className="px-8 py-3 bg-indigo-600 rounded-full font-black">Quay lại</button>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-slate-950 text-white overflow-hidden flex flex-col font-sans select-none">
      {/* Top Header */}
      <div className="absolute top-0 left-0 w-full p-6 flex items-center justify-between z-40 bg-gradient-to-b from-black/80 to-transparent pointer-events-none">
        <div className="flex items-center gap-4 pointer-events-auto">
          <button 
            onClick={() => navigate(-1)}
            className="w-12 h-12 rounded-2xl bg-white/10 hover:bg-white/20 backdrop-blur-md flex items-center justify-center transition-all border border-white/10"
          >
            <ChevronLeftIcon className="h-6 w-6" />
          </button>
          <div>
            <h1 className="text-xl font-black tracking-tight drop-shadow-lg">{appData.topic}</h1>
            <p className="text-xs text-indigo-300 font-bold tracking-widest uppercase drop-shadow-md">Bản trình chiếu 3D cao cấp</p>
          </div>
        </div>

        <div className="flex items-center gap-3 pointer-events-auto">
          <button 
            onClick={() => setShowInfo(!showInfo)}
            className={`w-12 h-12 rounded-2xl backdrop-blur-md flex items-center justify-center transition-all border border-white/10 ${showInfo ? 'bg-indigo-600 text-white shadow-lg shadow-indigo-500/50' : 'bg-white/10 hover:bg-white/20'}`}
          >
            <InfoIcon className="h-6 w-6" />
          </button>
          <button 
            onClick={toggleFullscreen}
            className="w-12 h-12 rounded-2xl bg-white/10 hover:bg-white/20 backdrop-blur-md flex items-center justify-center transition-all border border-white/10"
          >
            {isFullscreen ? <MinimizeIcon className="h-6 w-6" /> : <MaximizeIcon className="h-6 w-6" />}
          </button>
        </div>
      </div>

      {/* Main 3D Viewer */}
      <div className="flex-1 relative">
        <ThreeViewer 
          modelUrl={appData.modelUrl} 
          activeEntityId={activeEntityId}
          exploded={exploded}
          sectionView={sectionView}
          annotations={appData.annotations}
          onEntityClick={handleEntityClick}
        />

        {/* Floating Controls Side */}
        <div className="absolute top-1/2 right-6 -translate-y-1/2 flex flex-col gap-4 z-40">
          <button
            onClick={() => setExploded(!exploded)}
            className={`p-5 rounded-[2rem] border transition-all shadow-2xl backdrop-blur-xl group ${
              exploded ? 'bg-indigo-600 border-indigo-400 text-white' : 'bg-black/40 border-white/10 text-white/70 hover:bg-black/60 hover:text-white'
            }`}
            title="Tách rời mô hình"
          >
            <LayersIcon className="h-6 w-6 group-hover:scale-110 transition-transform" />
          </button>
          <button
            onClick={() => setSectionView(!sectionView)}
            className={`p-5 rounded-[2rem] border transition-all shadow-2xl backdrop-blur-xl group ${
              sectionView ? 'bg-indigo-600 border-indigo-400 text-white' : 'bg-black/40 border-white/10 text-white/70 hover:bg-black/60 hover:text-white'
            }`}
            title="Xem mặt cắt"
          >
            <EyeIcon className="h-6 w-6 group-hover:scale-110 transition-transform" />
          </button>
        </div>

        {/* Info Panel Overlay */}
        {showInfo && (
          <div className="absolute top-24 left-6 w-80 max-h-[70vh] overflow-y-auto bg-black/60 backdrop-blur-2xl border border-white/10 rounded-[2.5rem] p-8 z-40 animate-in slide-in-from-left-4 duration-300 custom-scrollbar">
            <h3 className="text-indigo-400 text-[10px] font-black uppercase tracking-[0.22em] mb-2">Giới thiệu mô hình</h3>
            <p className="text-sm leading-relaxed text-slate-200 mb-6 font-medium">{appData.description}</p>
            
            <h3 className="text-indigo-400 text-[10px] font-black uppercase tracking-[0.22em] mb-4">Các bộ phận</h3>
            <div className="space-y-2">
              {appData.hierarchy.map(entity => (
                <button 
                  key={entity.id}
                  onClick={() => setActiveEntityId(entity.id)}
                  className={`w-full text-left px-4 py-3 rounded-2xl text-xs font-bold transition-all ${activeEntityId === entity.id ? 'bg-indigo-600/30 text-indigo-300 border border-indigo-500/50' : 'bg-white/5 text-slate-400 hover:bg-white/10'}`}
                >
                  {entity.name}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Bottom Interaction Panel */}
        <div className="absolute bottom-8 left-1/2 -translate-x-1/2 w-[90%] max-w-4xl z-40 space-y-4">
          {/* Quiz UI if active */}
          {activeQuizId && (
            <div className="bg-slate-900/80 backdrop-blur-2xl border border-indigo-500/30 rounded-[2.5rem] p-6 shadow-2xl animate-in slide-in-from-bottom-4 duration-300">
              <div className="flex items-start gap-6">
                <div className="w-14 h-14 rounded-[1.5rem] bg-indigo-500/20 border border-indigo-400/30 flex items-center justify-center shrink-0">
                  <HelpCircleIcon className="h-7 w-7 text-indigo-400" />
                </div>
                <div className="flex-1 space-y-3">
                  <div className="flex items-center justify-between">
                    <p className="text-indigo-400 text-[10px] font-black uppercase tracking-widest">Thử thách định danh</p>
                    <button 
                      onClick={() => { setActiveQuizId(null); setQuizStatus('pending'); }}
                      className="text-slate-400 hover:text-white text-[10px] font-black uppercase tracking-widest"
                    >
                      Đóng
                    </button>
                  </div>
                  <p className="text-lg font-black leading-tight">
                    {appData.quizzes.find(q => q.id === activeQuizId)?.question}
                  </p>
                  
                  {quizStatus !== 'pending' && (
                    <div className={`p-4 rounded-2xl border flex items-center gap-3 animate-in fade-in slide-in-from-top-2 ${
                      quizStatus === 'correct' ? 'bg-emerald-500/20 border-emerald-400/30 text-emerald-400' : 'bg-rose-500/20 border-rose-400/30 text-rose-400'
                    }`}>
                      {quizStatus === 'correct' ? (
                        <CheckIcon className="h-5 w-5" />
                      ) : (
                        <div className="w-5 h-5 rounded-full border-2 border-rose-400 flex items-center justify-center font-black text-[10px]">!</div>
                      )}
                      <p className="text-sm font-bold">
                        {quizStatus === 'correct' 
                          ? `Chính xác! ${appData.quizzes.find(q => q.id === activeQuizId)?.feedback.correct}` 
                          : 'Chưa đúng rồi. Hãy chọn bộ phận khác trên mô hình 3D nhé!'}
                      </p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Main Action Bar */}
          <div className="flex items-center justify-between p-4 rounded-[3rem] bg-black/40 backdrop-blur-3xl border border-white/10 shadow-2xl">
            <div className="flex items-center gap-2">
              <div className="w-12 h-12 rounded-full bg-indigo-600 flex items-center justify-center shadow-lg shadow-indigo-500/20">
                <PlayCircleIcon className="h-6 w-6" />
              </div>
              <div className="px-4">
                <p className="text-[10px] font-black uppercase tracking-widest text-indigo-300">Hoạt hình</p>
                <p className="text-sm font-black tracking-tight">Kích hoạt vận hành</p>
              </div>
            </div>

            <div className="flex items-center gap-3">
              <div className="h-10 w-[1px] bg-white/10 mx-2" />
              {appData.quizzes.length > 0 && !activeQuizId && (
                <button 
                  onClick={() => setActiveQuizId(appData.quizzes[0].id)}
                  className="px-6 py-3 bg-white/10 hover:bg-white/20 rounded-full text-xs font-black transition-all flex items-center gap-2"
                >
                  <HelpCircleIcon className="h-4 w-4 text-indigo-400" />
                  Làm bài tập
                </button>
              )}
              <button 
                onClick={() => setExploded(!exploded)}
                className={`px-8 py-3 rounded-full text-xs font-black transition-all shadow-lg ${exploded ? 'bg-indigo-600 text-white shadow-indigo-500/30' : 'bg-white text-slate-900 hover:scale-105'}`}
              >
                {exploded ? 'Dừng mô phỏng' : 'Bắt đầu mô phỏng'}
              </button>
            </div>
          </div>
        </div>
      </div>
      
      <style>{`
        .custom-scrollbar::-webkit-scrollbar {
          width: 4px;
        }
        .custom-scrollbar::-webkit-scrollbar-track {
          background: transparent;
        }
        .custom-scrollbar::-webkit-scrollbar-thumb {
          background: rgba(255, 255, 255, 0.1);
          border-radius: 10px;
        }
        .custom-scrollbar::-webkit-scrollbar-thumb:hover {
          background: rgba(255, 255, 255, 0.2);
        }
      `}</style>
    </div>
  );
};
